import {
  ArrowsDownUpIcon,
  CaretDownIcon,
  MagnifyingGlassIcon,
  SlidersHorizontalIcon,
} from "@phosphor-icons/react";
import { times } from "lodash-es";
import { useIntl } from "react-intl";

import {
  FilterButton,
  FilterButtonSkeleton,
} from "@/components/FilterButton/FilterButton";
import { HeaderSearchButton } from "@/components/HeaderSearchButton/HeaderSearchButton";
import { StickyHeader } from "@/components/StickyHeader/StickyHeader";
import { HotelsListDrawerComponent } from "@/features/hotels/components/hotels-list/types";
import { useHotelSearchDataContext } from "@/features/hotels/contexts/HotelSearchDataContext";
import {
  HotelsSortOptions,
  type HotelsSortValue,
  type IHotelsFilters,
} from "@/features/hotels/types/types";
import { formatHeaderDate } from "@/utils/dates";

type Props = {
  onFilterButtonClick: (drawerComponent: HotelsListDrawerComponent) => void;
  selectedSortValue: HotelsSortValue;
  progress?: number;
  onSearchDrawerOpen: () => void;
  activeFilters:
    | (Partial<Record<keyof IHotelsFilters, boolean>> & {
        searchByName: boolean;
      })
    | null;
};

export function HotelListHeader({
  onFilterButtonClick,
  selectedSortValue,
  progress,
  activeFilters,
  onSearchDrawerOpen,
}: Props) {
  const { searchData } = useHotelSearchDataContext();
  const intl = useIntl();

  const sortOptionsLabels = {
    [HotelsSortOptions.PRICE]: intl.formatMessage({
      id: "hotels.filters.cheapestFirst",
      defaultMessage: "Cheapest first",
    }),
    [HotelsSortOptions.RATING]: intl.formatMessage({
      id: "hotels.filters.ratingFirst",
      defaultMessage: "Property Rating And Price",
    }),
    [HotelsSortOptions.RECOMMENDED]: intl.formatMessage({
      id: "hotels.filters.recommendedFirst",
      defaultMessage: "Recommended",
    }),
    [HotelsSortOptions.SPECIALDEAL]: intl.formatMessage({
      id: "hotels.filters.specialDealFirst",
      defaultMessage: "Special Deals",
    }),
  };

  return (
    <StickyHeader>
      <div className="flex items-center justify-between gap-2 py-2 text-text-primary">
        <div className="grow overflow-hidden">
          <HeaderSearchButton
            onClick={() => {
              onSearchDrawerOpen();
            }}
            progress={
              progress != null
                ? { variant: "determinate", value: progress }
                : undefined
            }
          >
            <h3 className="w-full truncate text-start font-medium text-text-title">
              {searchData?.destination?.name}
            </h3>

            <div className="w-full truncate text-xs font-normal text-text-tertiary">
              {[
                `${formatHeaderDate(searchData!.dates.checkIn)} - ${formatHeaderDate(searchData!.dates.checkOut)}`,
                intl.formatMessage(
                  {
                    id: "hotels.search.roomCount",
                    defaultMessage:
                      "{count, plural, one {# room} other {# rooms}}",
                  },
                  { count: searchData!.rooms.length },
                ),
              ].map((item, index) => (
                <span key={index}>
                  {index > 0 ? <> &bull; </> : null}
                  {item}
                </span>
              ))}
            </div>
          </HeaderSearchButton>
        </div>
      </div>

      <div className="mb-2 flex gap-1 overflow-x-scroll pt-px pb-2">
        {progress !== undefined ? (
          times(4, (idx) => <FilterButtonSkeleton key={idx} />)
        ) : (
          <>
            <FilterButton
              onClick={() =>
                onFilterButtonClick(HotelsListDrawerComponent.SORT)
              }
            >
              {sortOptionsLabels[selectedSortValue.field]}
              <ArrowsDownUpIcon size={20} className="fill-text-primary" />
            </FilterButton>
            <FilterButton
              onClick={() =>
                onFilterButtonClick(HotelsListDrawerComponent.ALL_FILTERS)
              }
              active={Object.values(activeFilters || {}).some(Boolean)}
            >
              {intl.formatMessage({
                id: "hotels.filters.allFilters",
                defaultMessage: "All filters",
              })}
              <SlidersHorizontalIcon size={20} className="fill-text-primary" />
            </FilterButton>
            <FilterButton
              onClick={() =>
                onFilterButtonClick(HotelsListDrawerComponent.SEARCH_BY_NAME)
              }
              active={activeFilters?.searchByName || false}
            >
              {intl.formatMessage({
                id: "hotels.filters.searchByName",
                defaultMessage: "Search by name",
              })}
              <MagnifyingGlassIcon size={20} className="fill-text-primary" />
            </FilterButton>
            <FilterButton
              onClick={() => {
                onFilterButtonClick(HotelsListDrawerComponent.HOTEL_CLASS);
              }}
              active={activeFilters?.starRating || false}
            >
              {intl.formatMessage({
                id: "hotels.filters.hotelClass",
                defaultMessage: "Hotel Class",
              })}
              <CaretDownIcon size={20} />
            </FilterButton>
          </>
        )}
      </div>
    </StickyHeader>
  );
}
